<template>
    <div class="rd">
        <div class="pagina">
            <header class="paginakop">
                <div class="tekst">
                    <nav class="kruimel" aria-label="Breadcrumb">
                        <router-link to="/applications">Applications</router-link>
                        <span>/</span>
                        <span>{{ app ? app.repository : "Application" }}</span>
                    </nav>
                    <h1>{{ app ? app.repository : "Application" }}</h1>
                    <p>Pick a version to activate it on a device. Use a column header to activate that version on every device shown.</p>
                </div>
                <div class="rechts">
                    <router-link class="btn btn-stil btn-sm" to="/applications">All applications</router-link>
                </div>
            </header>

            <template v-if="!loading">
                <section class="paneel filters">
                    <div class="filter-rij">
                        <div class="veld">
                            <label for="ad-status">Device status</label>
                            <select id="ad-status" v-model="statusId">
                                <option v-for="opt in ACTIVE_OPTIONS" :key="opt.id" :value="opt.id">{{ opt.name }}</option>
                            </select>
                        </div>
                        <div class="veld f-loc">
                            <label for="ad-loc">Location</label>
                            <select id="ad-loc" v-model="locationId">
                                <option value="all">All locations</option>
                                <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
                            </select>
                        </div>
                        <div class="veld">
                            <label for="ad-land">Country</label>
                            <select id="ad-land" v-model="countryId">
                                <option value="all">All countries</option>
                                <option v-for="c in sortedCountries" :key="c.id" :value="c.id">{{ c.name }}</option>
                            </select>
                        </div>
                        <div class="veld">
                            <label for="ad-type">Device type</label>
                            <select id="ad-type" v-model="typeId">
                                <option v-for="opt in TYPE_OPTIONS" :key="opt.id" :value="opt.id">{{ opt.name }}</option>
                            </select>
                        </div>
                    </div>
                </section>

                <section class="paneel">
                    <div class="paneel-kop" style="flex-wrap:wrap">
                        <h2>Versions per device</h2>
                        <div class="rechts legenda">
                            <span><span class="punt actief"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg></span>Running</span>
                            <span><span class="punt bezig" />Activating</span>
                            <span><span class="punt laden" />Downloading</span>
                            <span><span class="punt installed" />Installed</span>
                            <span><span class="punt fout" />Problem</span>
                            <span><span class="punt geen" />Not installed</span>
                        </div>
                    </div>

                    <div v-if="allXCubes.length" class="tabel-wikkel">
                        <table class="tabel matrix">
                            <thead>
                                <tr>
                                    <th>Location</th>
                                    <th>Device</th>
                                    <th>Type</th>
                                    <th v-for="version in allVersions" :key="version" class="midden">
                                        <span class="opties">
                                            <button class="versiekop" type="button" :aria-expanded="openHeader === version" @click.stop="toggleHeader(version)">{{ version }}</button>
                                            <div v-show="openHeader === version" class="uitklap">
                                                <button type="button" @click="headerAction(version, 'activate')">Activate on all shown</button>
                                                <button type="button" class="rood" @click="headerAction(version, 'delete')">Delete version</button>
                                            </div>
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in allXCubes" :key="row.id">
                                    <td class="nowrap">{{ row.location && row.location.name }}</td>
                                    <td class="num nowrap">{{ row.name }}</td>
                                    <td>{{ row.type }}</td>
                                    <td v-for="version in allVersions" :key="version" class="midden">
                                        <span v-if="getStatus(row.id, version)" class="opties">
                                            <button class="versiecel" type="button" :aria-label="`${version} on ${row.name}: ${getStatus(row.id, version)}`" @click.stop="toggleCell(row.id, version)">
                                                <span class="punt" :class="puntClass(getStatus(row.id, version))" :title="getStatus(row.id, version)">
                                                    <svg v-if="puntClass(getStatus(row.id, version)) === 'actief'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                                                </span>
                                            </button>
                                            <div v-show="openCell === cellKey(row.id, version)" class="uitklap">
                                                <button v-if="canActivate(getStatus(row.id, version))" type="button" @click="cellAction(row.id, version, 'activate')">Activate</button>
                                                <button v-else type="button" @click="cellAction(row.id, version, 'deactivate')">Deactivate</button>
                                                <button type="button" class="rood" @click="cellAction(row.id, version, 'delete')">Delete</button>
                                            </div>
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else class="leeg">
                        <h3>No devices match these filters</h3>
                        <p>Clear a filter or set Device status to All.</p>
                    </div>
                </section>
            </template>

            <div v-else class="leeg"><h2>Loading application…</h2></div>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router/composables";
import {
    deactivateXCubeApplication,
    deleteXCubeApplication,
    getApplication,
    getXCubeApplications,
    getXCubeApplicationsForXCubeAndApplication,
} from "../../api/graphql/applications";
import { getXCubesSimple } from "../../api/graphql/xcubes";
import { getUserLocationsSimple } from "../../api/graphql/locations";
import { activateApplicationVersion, deleteApplicationVersion } from "../../api/rest";
import { Message, MessageBox } from "element-ui";

const ALL_OPTION = { name: "All", id: "all" };

const ACTIVE_OPTIONS = [
    ALL_OPTION,
    { name: "Active", id: "active" },
    { name: "Inactive", id: "inactive" },
];

const TYPE_OPTIONS = [
    { name: "XCube", id: "xcube" },
    { name: "XPod", id: "xpod" },
    { name: "Kiosk", id: "xkiosk" },
    { name: "Xtend", id: "xtend" },
    ALL_OPTION,
];

const route = useRoute();
const app = ref(null);
const xCubeApplications = ref([]);
const allXCubes = ref([]);
const loading = ref(true);
const locations = ref([]);
const openCell = ref(null);
const openHeader = ref(null);

const filter = ref({
    active: { name: "Active", id: "active" },
    location: ALL_OPTION,
    type: ALL_OPTION,
    country: ALL_OPTION,
});

const sortedCountries = computed(() => {
    const set = locations.value.reduce((acc, loc) => {
        if (loc.country) {
            acc.add(loc.country.toUpperCase());
        }
        return acc;
    }, new Set());
    return Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((c) => ({ name: c, id: c }));
});

// Native <select> works with string values; map each to the {name,id} object the filter holds.
const statusId = computed({
    get: () => filter.value.active?.id ?? "all",
    set: (id) => {
        filter.value.active = ACTIVE_OPTIONS.find((o) => o.id === id) ?? ALL_OPTION;
    },
});
const typeId = computed({
    get: () => filter.value.type?.id ?? "all",
    set: (id) => {
        filter.value.type = TYPE_OPTIONS.find((o) => o.id === id) ?? ALL_OPTION;
    },
});
const locationId = computed({
    get: () => filter.value.location?.id ?? "all",
    set: (id) => {
        filter.value.location =
            id === "all" ? ALL_OPTION : locations.value.find((l) => String(l.id) === String(id)) ?? ALL_OPTION;
    },
});
const countryId = computed({
    get: () => filter.value.country?.id ?? "all",
    set: (id) => {
        filter.value.country = id === "all" ? ALL_OPTION : { name: id, id };
    },
});

const getStatus = (xcubeId, version) => {
    const result = xCubeApplications.value?.find(
        (x) => x.version === version && x.x_cube.id === xcubeId.toString(),
    );
    return result?.status;
};

// Map each instance status to a legend dot; the exact API status stays in the cell title.
// active → running, pending-active → activating, downloading → downloading,
// *_error → problem, everything else (inactive/downloaded/…) → installed.
const puntClass = (status) => {
    if (status === "active") {
        return "actief";
    }
    if (status === "pending-active") {
        return "bezig";
    }
    if (status === "downloading") {
        return "laden";
    }
    if (typeof status === "string" && status.endsWith("_error")) {
        return "fout";
    }
    return "installed";
};

const canActivate = (status) => !["pending-active", "active"].includes(status);

const cellKey = (xcubeId, version) => `${xcubeId}|${version}`;

const toggleCell = (xcubeId, version) => {
    openHeader.value = null;
    const key = cellKey(xcubeId, version);
    openCell.value = openCell.value === key ? null : key;
};

const toggleHeader = (version) => {
    openCell.value = null;
    openHeader.value = openHeader.value === version ? null : version;
};

const closeMenus = () => {
    openCell.value = null;
    openHeader.value = null;
};

const allVersions = computed(() => {
    if (xCubeApplications.value.length > 0) {
        const set = xCubeApplications.value?.reduce((acc, x) => acc.add(x.version), new Set());
        return Array.from(set).toSorted((a, b) => (a > b ? -1 : 1));
    }
    return [];
});

const cleanedFilter = computed(() => {
    const value = {};
    if (filter.value.active?.id && filter.value.active.id !== ALL_OPTION.id) {
        value.status = filter.value.active.id;
    }
    if (filter.value.location?.id && filter.value.location.id !== ALL_OPTION.id) {
        value.location = filter.value.location.id;
    }
    if (filter.value.type?.id && filter.value.type.id !== ALL_OPTION.id) {
        value.type = filter.value.type.id;
    }
    if (filter.value.country?.id && filter.value.country.id !== ALL_OPTION.id) {
        value.country = filter.value.country.id;
    }
    return value;
});

const getFilteredDevices = async () => getXCubesSimple(cleanedFilter.value);

const refreshData = async () => {
    loading.value = true;
    xCubeApplications.value = await getXCubeApplications(route.params.id);
    allXCubes.value = await getFilteredDevices();
    loading.value = false;
};

const refreshIndividualData = async (applicationId, xcubeId) => {
    const result = await getXCubeApplicationsForXCubeAndApplication(xcubeId, applicationId);
    xCubeApplications.value = xCubeApplications.value.map((x) => {
        const replacement = result.find((r) => r.id === x.id);
        return replacement ? replacement : x;
    });
};

// Column header: activate this version across every device shown (with a force fallback
// when the app is not downloaded), or delete the version entirely.
const headerAction = async (version, action) => {
    closeMenus();
    try {
        if (action === "activate") {
            try {
                await activateApplicationVersion(route.params.id, version, cleanedFilter.value);
            } catch (error) {
                if (error.message.includes("Some xCubes do not have the application inactive")) {
                    await MessageBox.confirm(
                        "Some X-Cubes do not have the application downloaded. Force activation on all matching X-Cubes?",
                        "Force activation",
                        { confirmButtonText: "Yes, force it", cancelButtonText: "Cancel", type: "warning" },
                    );
                    await activateApplicationVersion(route.params.id, version, {
                        ...cleanedFilter.value,
                        ignore_missing: true,
                    });
                } else {
                    throw error;
                }
            }
            await refreshData();
            Message.success(`${version} activated on the shown devices`);
        } else if (action === "delete") {
            await MessageBox.confirm(
                `Delete version ${version} of this application everywhere?`,
                "Delete version",
                { confirmButtonText: "Delete", cancelButtonText: "Cancel", type: "warning" },
            );
            await deleteApplicationVersion(route.params.id, version);
            await refreshData();
            Message.success(`Version ${version} deleted`);
        }
    } catch (error) {
        if (error === "cancel" || error === "close") {
            return;
        }
        Message.error(`Could not ${action} ${version}: ${error.message}`);
    }
};

// One cell: activate / deactivate / delete this version on this one device.
const cellAction = async (xcubeId, version, action) => {
    closeMenus();
    try {
        const xcubeApplication = xCubeApplications.value.find(
            (x) => x.version === version && x.x_cube.id === xcubeId.toString(),
        );
        if (!xcubeApplication) {
            throw new Error(`No instance found for ${version}`);
        }
        if (action === "activate") {
            await activateApplicationVersion(route.params.id, version, { xcubeId });
        } else if (action === "deactivate") {
            await deactivateXCubeApplication(xcubeApplication.id);
        } else if (action === "delete") {
            await MessageBox.confirm(
                `Delete ${version} from this device?`,
                "Delete",
                { confirmButtonText: "Delete", cancelButtonText: "Cancel", type: "warning" },
            );
            await deleteXCubeApplication(xcubeApplication.id);
        }
        await refreshIndividualData(route.params.id, xcubeId);
        Message.success(`${action} done for ${version}`);
    } catch (error) {
        if (error === "cancel" || error === "close") {
            return;
        }
        Message.error(`Could not ${action} ${version}: ${error.message}`);
    }
};

onMounted(async () => {
    loading.value = true;
    const result = await Promise.all([
        getApplication(route.params.id),
        getFilteredDevices(),
        getXCubeApplications(route.params.id),
        getUserLocationsSimple(),
    ]);
    [app.value, allXCubes.value, xCubeApplications.value, locations.value] = result;
    loading.value = false;
    document.addEventListener("click", closeMenus);
});

onUnmounted(() => {
    document.removeEventListener("click", closeMenus);
});

watch(
    filter,
    async () => {
        loading.value = true;
        allXCubes.value = await getFilteredDevices();
        loading.value = false;
    },
    { deep: true },
);
</script>
